import styles from './PricingTableHeadColumn.module.scss';

import React from 'react';

import cx from 'classnames';
import { ContentImage } from 'components/ContentImage/ContentImage';
import { useFeatureFlagsContext } from 'components/FeatureFlags/FeatureFlags';
import { TextV2 } from 'components/TextV2/TextV2';
import { useBreakpoints } from 'hooks/useBreakpoints';
import type { ICartPricing } from 'hooks/useDCSTypes';
import type { Bundle } from 'services/cms/bundleTypes';
import type { TagFields } from 'components/PricingDetailTable/types';
import { PRICING_TABLE_HEAD_COLUMN_TEST_ID } from './PricingTableHeadColumn.const';
import { BundlePricing } from 'components/Pricing/BundlePricing/BundlePricing';

type PricingTableHeadColumnProps = Pick<Bundle, 'title' | 'description' | 'image' | 'id'> &
  TagFields &
  React.ThHTMLAttributes<HTMLTableCellElement> & { colSpanCount?: number; pricings?: ICartPricing | null };

export const PricingTableHeadColumn: React.FC<PricingTableHeadColumnProps> = ({
  title,
  description,
  image,
  id,
  showTag,
  tagText,
  tagBackgroundColor,
  bundleWithTag,
  colSpanCount,
  pricings,
  ...props
}) => {
  const { browser } = useBreakpoints();
  const isDesktopOrLarge = browser.is.desktop;

  const { isFlagEnabled } = useFeatureFlagsContext();
  const isDescriptionEnabled = isFlagEnabled('show-product-description-global');

  const isThisBundleWithTag = bundleWithTag && bundleWithTag.id === id;
  const tagShouldBeShown = isDesktopOrLarge && isThisBundleWithTag && showTag && tagText;

  return (
    <th
      className={styles.headColumnCell}
      colSpan={colSpanCount}
      scope="col"
      {...props}
      id={`bundle-${id}`}
      aria-labelledby={`bundle-text-${id}`}
    >
      {tagShouldBeShown && (
        <TextV2
          className={styles.headColumnTag}
          style={{ backgroundColor: tagBackgroundColor }}
          data-testid={PRICING_TABLE_HEAD_COLUMN_TEST_ID.tag}
          value={tagText}
          variant="eyebrow"
        />
      )}

      <div className={styles.headColumnPriceContainer}>
        <ContentImage
          entry={image}
          className={styles.headColumnImage}
          data-testid={PRICING_TABLE_HEAD_COLUMN_TEST_ID.image}
          alt=""
        />
        <TextV2
          className={styles.headColumnTitle}
          data-testid={PRICING_TABLE_HEAD_COLUMN_TEST_ID.title}
          value={title}
          variant="eyebrow"
          id={`bundle-text-${id}`}
        />
        {!isDesktopOrLarge && (
          <>
            {isDescriptionEnabled && (
              <TextV2
                className={styles.headColumnDescription}
                data-testid={PRICING_TABLE_HEAD_COLUMN_TEST_ID.description}
                value={description}
                variant="body"
                tagName="p"
              />
            )}
            <div className={cx({ [styles.headColumnPriceDisplay]: !description })}>
              {pricings && <BundlePricing pricing={pricings} />}
            </div>
          </>
        )}
      </div>
    </th>
  );
};
